/**
 * Home Assistant Entity Store
 *
 * Per-instance map of HA entities backed by:
 *   - REST initial load from the backend relay
 *   - Live entity_state_update stream over Socket.IO `/ha-stream`
 *
 * Mirrors the standard Zustand+immer+socket pattern used by device-store.
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import { io, Socket } from 'socket.io-client';
import { config } from '@/config';
import { getCurrentAccessToken } from '@/services/api/auth-session';
import {
  getEntities as fetchEntities,
  getInstances as fetchInstances,
  callService as restCallService,
  getAutomation as fetchAutomation,
  type HAEntity,
  type HAInstance,
} from '@/services/api/haApi';
import type { HAAutomationConfig, WhenIfDoModel } from '@/types/haAutomation';
import { toWhenIfDo } from '@/services/homeAssistant/automationProjection';

type InstanceStatus = 'online' | 'offline' | 'pending' | 'unknown';

interface HAStoreState {
  // Per-instance state. Each map is keyed by instanceId.
  instances: Record<string, HAInstance>;
  entitiesByInstance: Record<string, Record<string, HAEntity>>;
  statusByInstance: Record<string, InstanceStatus>;
  loadingByInstance: Record<string, boolean>;
  errorByInstance: Record<string, string | null>;

  // Connection-level state.
  socketConnected: boolean;
  socketError: string | null;

  // Automation editing slice. `automationEditable` is the AUTHORITATIVE
  // open-time flag from GET-one (NOT the list hint). `automationBaseHash` is
  // the SERVER-issued drift hash — echoed back on update, never recomputed
  // (Audit §A1/§A3).
  selectedAutomation: HAAutomationConfig | null;
  selectedAutomationId: string | null;
  automationEditable: boolean;
  automationBaseHash: string | null;
  automationWorkingModel: WhenIfDoModel | null;
  automationLoading: boolean;
  automationError: string | null;

  // Actions.
  connect: () => void;
  disconnect: () => void;
  loadInstances: () => Promise<void>;
  loadEntities: (instanceId: string, domain?: string) => Promise<void>;
  subscribeToInstance: (instanceId: string) => void;
  unsubscribeFromInstance: (instanceId: string) => void;
  callService: (
    instanceId: string,
    domain: string,
    service: string,
    entityId: string,
    data?: Record<string, unknown>,
  ) => Promise<void>;
  loadAutomation: (instanceId: string, automationId: string) => Promise<void>;
  clearAutomation: () => void;
}

// Module-scoped socket so HMR doesn't multiply connections.
let socket: Socket | null = null;

interface EntityStateUpdatePayload {
  instanceId: string;
  entityId: string;
  state: string;
  attributes: Record<string, unknown>;
  lastChanged: string;
}

interface InstanceStatusPayload {
  instanceId: string;
  status: InstanceStatus;
}

interface SubscriptionRevokedPayload {
  instanceId: string;
  reason?: string;
}

interface SubscribeAck {
  success: boolean;
  error?: string;
}

function getExplicitSubscriptionOrganizationId(
  instanceId: string,
  instances: Record<string, HAInstance>,
): string | null {
  const instanceOrgId = instances[instanceId]?.orgId;
  return typeof instanceOrgId === 'string' && instanceOrgId.length > 0
    ? instanceOrgId
    : null;
}

export const useHAStore = create<HAStoreState>()(
  devtools(
    immer((set, get) => ({
      instances: {},
      entitiesByInstance: {},
      statusByInstance: {},
      loadingByInstance: {},
      errorByInstance: {},
      socketConnected: false,
      socketError: null,
      selectedAutomation: null,
      selectedAutomationId: null,
      automationEditable: false,
      automationBaseHash: null,
      automationWorkingModel: null,
      automationLoading: false,
      automationError: null,

      connect: () => {
        if (socket?.connected || socket?.active) return;

        const token = getCurrentAccessToken();
        if (!token) {
          set((draft) => {
            draft.socketError = 'no access token available';
          });
          return;
        }

        socket = io(`${config.websocket.url}/ha-stream`, {
          auth: { token },
          transports: ['websocket', 'polling'],
          reconnection: true,
          reconnectionDelay: 1000,
          reconnectionDelayMax: 30_000,
        });

        socket.on('connect', () => {
          set((draft) => {
            draft.socketConnected = true;
            draft.socketError = null;
          });
          // Re-subscribe to any instances the caller had registered before
          // the (re)connection.
          for (const instanceId of Object.keys(get().statusByInstance)) {
            const organizationId = getExplicitSubscriptionOrganizationId(
              instanceId,
              get().instances,
            );
            if (organizationId) {
              socket?.emit('subscribe_instance', { instanceId, organizationId });
            }
          }
        });

        socket.on('disconnect', () => {
          set((draft) => {
            draft.socketConnected = false;
          });
        });

        socket.on('connect_error', (err: Error) => {
          set((draft) => {
            draft.socketError = err.message;
          });
        });

        socket.on('entity_state_update', (update: EntityStateUpdatePayload) => {
          set((draft) => {
            const bucket = draft.entitiesByInstance[update.instanceId];
            if (!bucket) return;
            const existing = bucket[update.entityId];
            if (existing) {
              existing.state = update.state;
              existing.attributes = update.attributes;
              existing.last_changed = update.lastChanged;
              existing.last_updated = update.lastChanged;
            } else {
              bucket[update.entityId] = {
                entity_id: update.entityId,
                state: update.state,
                attributes: update.attributes,
                last_changed: update.lastChanged,
                last_updated: update.lastChanged,
                domain: update.entityId.split('.')[0] ?? '',
              };
            }
          });
        });

        socket.on('instance_status', (payload: InstanceStatusPayload) => {
          set((draft) => {
            draft.statusByInstance[payload.instanceId] = payload.status;
          });
        });

        socket.on('subscription_revoked', (payload: SubscriptionRevokedPayload) => {
          set((draft) => {
            draft.errorByInstance[payload.instanceId] =
              payload.reason ?? 'Home Assistant subscription revoked';
            delete draft.statusByInstance[payload.instanceId];
          });
        });
      },

      disconnect: () => {
        if (socket) {
          socket.disconnect();
          socket = null;
        }
        set((draft) => {
          draft.socketConnected = false;
        });
      },

      loadInstances: async () => {
        try {
          const instances = await fetchInstances();
          set((draft) => {
            draft.instances = Object.fromEntries(instances.map((i) => [i._id, i]));
            for (const i of instances) {
              draft.statusByInstance[i._id] ??= i.status;
            }
          });
        } catch (err) {
          set((draft) => {
            draft.socketError = err instanceof Error ? err.message : String(err);
          });
        }
      },

      loadEntities: async (instanceId, domain) => {
        set((draft) => {
          draft.loadingByInstance[instanceId] = true;
          draft.errorByInstance[instanceId] = null;
        });
        try {
          const entities = await fetchEntities(instanceId, domain);
          set((draft) => {
            const bucket: Record<string, HAEntity> = {};
            for (const e of entities) {
              bucket[e.entity_id] = e;
            }
            draft.entitiesByInstance[instanceId] = bucket;
            draft.loadingByInstance[instanceId] = false;
          });
        } catch (err) {
          set((draft) => {
            draft.errorByInstance[instanceId] = err instanceof Error ? err.message : String(err);
            draft.loadingByInstance[instanceId] = false;
          });
        }
      },

      subscribeToInstance: (instanceId) => {
        set((draft) => {
          draft.statusByInstance[instanceId] ??= 'unknown';
        });
        if (!socket?.connected) {
          get().connect();
          return; // The connect handler will replay subscriptions
        }
        const organizationId = getExplicitSubscriptionOrganizationId(
          instanceId,
          get().instances,
        );
        if (!organizationId) {
          set((draft) => {
            draft.errorByInstance[instanceId] = 'organization context required';
          });
          return;
        }
        socket.emit('subscribe_instance', { instanceId, organizationId }, (ack: SubscribeAck) => {
          if (!ack?.success) {
            set((draft) => {
              draft.errorByInstance[instanceId] = ack?.error ?? 'subscribe failed';
            });
          }
        });
      },

      unsubscribeFromInstance: (instanceId) => {
        socket?.emit('unsubscribe_instance', { instanceId });
        set((draft) => {
          delete draft.statusByInstance[instanceId];
        });
      },

      callService: async (instanceId, domain, service, entityId, data) => {
        await restCallService(instanceId, domain, service, entityId, data);
      },

      loadAutomation: async (instanceId, automationId) => {
        set((draft) => {
          draft.automationLoading = true;
          draft.automationError = null;
        });
        try {
          const { config, editable, baseHash } = await fetchAutomation(
            instanceId,
            automationId,
          );
          set((draft) => {
            draft.selectedAutomation = config;
            draft.selectedAutomationId = automationId;
            draft.automationEditable = editable;
            draft.automationBaseHash = baseHash;
            draft.automationWorkingModel = config ? toWhenIfDo(config) : null;
            draft.automationLoading = false;
          });
        } catch (err) {
          set((draft) => {
            draft.automationError = err instanceof Error ? err.message : String(err);
            draft.automationLoading = false;
          });
        }
      },

      clearAutomation: () => {
        set((draft) => {
          draft.selectedAutomation = null;
          draft.selectedAutomationId = null;
          draft.automationEditable = false;
          draft.automationBaseHash = null;
          draft.automationWorkingModel = null;
          draft.automationError = null;
        });
      },
    })),
    { name: 'ha-store' },
  ),
);

/** Selector helper: return entities for an instance as a sorted array. */
export const selectEntitiesArray = (state: HAStoreState, instanceId: string): HAEntity[] => {
  const bucket = state.entitiesByInstance[instanceId];
  if (!bucket) return [];
  return Object.values(bucket).sort((a, b) => a.entity_id.localeCompare(b.entity_id));
};
